import { storage } from '../../../utils/storage.js';
import { ensureFlagsSpriteLoaded } from '../../../services/voiceService.js';
import { createCustomVoiceSelect } from '../../../components/customVoiceSelect.js';
import {
  LANGUAGE_FLAGS,
  LANGUAGE_NAMES,
  VOICE_LANGUAGE_NAME_TO_CODE,
  getLanguageFlag,
  getLanguageName,
  getVoiceLanguageCodesForProvider,
} from '../../../utils/languageData.js';

export const LANGUAGE_MAP = VOICE_LANGUAGE_NAME_TO_CODE;
export const LANGUAGE_TO_FLAG = LANGUAGE_FLAGS;
export const LANGUAGE_TO_NAME = LANGUAGE_NAMES;

const resolveCharacterVoiceProvider = (characterId) => {
  const providers = storage.get('characterVoiceProviders') || {};
  return providers[characterId] || 'elevenlabs';
};

export const getDefaultVoiceLanguage = (supportedCodes = [], savedCode = null) => {
  if (savedCode && supportedCodes.includes(savedCode)) return savedCode;
  if (supportedCodes.includes('en')) return 'en';
  if (supportedCodes.includes('ja')) return 'ja';
  return supportedCodes[0] || 'en';
};

export const persistCharacterVoiceLanguage = (characterId, code) => {
  const voicePreferences = storage.get('characterVoiceLanguages') || {};
  if (code === 'en') {
    delete voicePreferences[characterId];
  } else {
    voicePreferences[characterId] = code;
  }
  storage.set('characterVoiceLanguages', voicePreferences);
};

export const createVoiceLanguageFlagsEl = (codes = []) => {
  const wrap = document.createElement('div');
  wrap.className = 'voice-flags';
  wrap.setAttribute('aria-hidden', 'true');

  codes.forEach((code) => {
    const flagSymbolId = getLanguageFlag(code);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'voice-flag');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '15');
    svg.setAttribute('viewBox', '0 0 640 480');
    svg.setAttribute('aria-hidden', 'true');

    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#${flagSymbolId}`);
    svg.appendChild(use);
    wrap.appendChild(svg);
  });

  return wrap;
};

const buildLanguageOptionContent = (code) => {
  const row = document.createElement('div');
  row.className = 'voice-option-content';

  const name = document.createElement('span');
  name.className = 'voice-name';
  name.textContent = getLanguageName(code) || code;

  row.appendChild(name);
  row.appendChild(createVoiceLanguageFlagsEl([code]));
  return row;
};

const buildLanguageSelectedContent = (code) => {
  const frag = document.createDocumentFragment();

  const name = document.createElement('span');
  name.className = 'voice-name';
  name.textContent = getLanguageName(code) || code;
  frag.appendChild(name);

  const flags = createVoiceLanguageFlagsEl([code]);
  flags.classList.add('voice-flags-selected');
  frag.appendChild(flags);

  return frag;
};

const buildLanguageOptions = (codes) => codes.map((code) => ({
  value: code,
  renderOption: () => buildLanguageOptionContent(code),
  renderSelected: () => buildLanguageSelectedContent(code),
}));

export const syncFixedVoiceLanguageControl = ({ character, controlEl, provider = null }) => {
  if (!controlEl) return;
  const resolvedProvider = provider || resolveCharacterVoiceProvider(character.id);
  const supportedCodes = getVoiceLanguageCodesForProvider(resolvedProvider);
  controlEl.dataset.voiceProvider = resolvedProvider;

  if (typeof controlEl._setOptions === 'function') {
    const voicePreferences = storage.get('characterVoiceLanguages') || {};
    const savedLanguage = voicePreferences[character.id] || 'en';
    const languageToUse = getDefaultVoiceLanguage(supportedCodes, savedLanguage);
    controlEl._setOptions(buildLanguageOptions(supportedCodes), languageToUse);
    persistCharacterVoiceLanguage(character.id, languageToUse);
    return;
  }

  const voicePreferences = storage.get('characterVoiceLanguages') || {};
  const savedLanguage = voicePreferences[character.id] || 'en';
  const languageToUse = getDefaultVoiceLanguage(supportedCodes, savedLanguage);
  persistCharacterVoiceLanguage(character.id, languageToUse);
  if (typeof controlEl._setValue === 'function') {
    controlEl._setValue(languageToUse);
  }
};

/**
 * Language dropdown only (no wrapping form-group) for embedding in a shared voice controls group.
 * @param {Object} character
 * @param {{ provider?: string | null }} [options]
 */
export const createVoiceLanguageControl = async (character, options = {}) => {
  await ensureFlagsSpriteLoaded();

  const provider = options.provider || resolveCharacterVoiceProvider(character.id);
  const supportedCodes = getVoiceLanguageCodesForProvider(provider);

  const container = createCustomVoiceSelect({
    className: 'custom-voice-select character-voice-language-control',
    ariaLabel: 'Voice Language',
    closeScopeSelector: '.voice-controls-group',
    options: buildLanguageOptions(supportedCodes),
    onSelect: (code) => {
      const currentCodes = getVoiceLanguageCodesForProvider(
        container.dataset.voiceProvider || provider
      );
      const nextCode = currentCodes.includes(code) ? code : 'en';
      persistCharacterVoiceLanguage(character.id, nextCode);
    },
  });

  container.setAttribute('data-character-id', character.id);
  container.dataset.voiceProvider = provider;
  syncFixedVoiceLanguageControl({ character, controlEl: container, provider });
  return container;
};
